import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import CategoryList from './CategoryList';
import ToleranceForm from './ToleranceForm';
import { toleranceApi } from '../helpers/toleranceApi';

const ToleranceSettingsModal = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [toleranceSettings, setToleranceSettings] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesData, settingsData] = await Promise.all([
        toleranceApi.getCategories(),
        toleranceApi.getToleranceSettings()
      ]);
      
      console.log('Categories fetched:', categoriesData);
      console.log('Tolerance settings fetched:', settingsData);
      
      setCategories(categoriesData);
      setToleranceSettings(settingsData);
      
      // Force initial render
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error fetching tolerance data:', error);
      alert('Error loading tolerance data. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTolerance = async (categoryName, toleranceData) => {
    setSaving(true);
    try {
      // Check if setting already exists
      const existingSetting = toleranceSettings.find(s => s.category === categoryName);
      
      let result;
      if (existingSetting) {
        console.log('Updating existing setting:', existingSetting._id, toleranceData);
        result = await toleranceApi.updateTolerance(existingSetting._id, toleranceData);
      } else {
        console.log('Creating new setting:', categoryName, toleranceData);
        result = await toleranceApi.createTolerance({ 
          category: categoryName, 
          ...toleranceData 
        });
      }

      console.log('API result:', result);

      // Update local state
      if (existingSetting) {
        setToleranceSettings(prev => {
          const updated = prev.map(s => s._id === existingSetting._id ? { ...s, ...result } : s);
          console.log('Updated tolerance settings (existing):', updated);
          return updated;
        });
      } else {
        setToleranceSettings(prev => {
          const updated = [...prev, result];
          console.log('Updated tolerance settings (new):', updated);
          return updated;
        });
      }

      // Force re-render by updating refresh key
      setRefreshKey(prev => prev + 1);

      // Show success message
      alert('Tolerance setting saved successfully!');
    } catch (error) {
      console.error('Error saving tolerance:', error);
      alert('Error saving tolerance setting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTolerance = async (categoryName) => {
    const setting = toleranceSettings.find(s => s.category === categoryName);
    if (!setting) return;

    if (!window.confirm(`Are you sure you want to delete tolerance setting for ${categoryName}?`)) {
      return;
    }

    try {
      await toleranceApi.deleteTolerance(setting._id);
      setToleranceSettings(prev => prev.filter(s => s._id !== setting._id));
      setSelectedCategory(null);
      
      // Force re-render by updating refresh key
      setRefreshKey(prev => prev + 1);
      
      alert('Tolerance setting deleted successfully!');
    } catch (error) {
      console.error('Error deleting tolerance:', error);
      alert('Error deleting tolerance setting. Please try again.');
    }
  };

  // Get tolerance setting for a category
  const getToleranceForCategory = (categoryName) => {
    return toleranceSettings.find(s => s.category === categoryName);
  };

  const handleClose = () => {
    setSelectedCategory(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="z-[1600] max-h-[90vh] w-[95vw] max-w-[1200px] mx-auto bg-white border border-gray-200 shadow-xl flex flex-col">
        <div className="p-6 flex-shrink-0">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Tolerance Settings</h2>
            <p className="text-gray-600">Manage inventory accuracy tolerance thresholds by category</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">Loading tolerance settings...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[500px]">
              {/* Left Panel - Category List */}
              <div className="lg:col-span-1">
                <CategoryList
                  key={refreshKey}
                  categories={categories}
                  toleranceSettings={toleranceSettings}
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                  getToleranceForCategory={getToleranceForCategory}
                />
              </div>

              {/* Right Panel - Tolerance Form */}
              <div className="lg:col-span-2">
                <ToleranceForm
                  selectedCategory={selectedCategory}
                  toleranceSetting={selectedCategory ? getToleranceForCategory(selectedCategory) : null}
                  onSave={handleSaveTolerance}
                  onDelete={handleDeleteTolerance}
                  saving={saving}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 px-6 pb-6">
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => fetchData()}
                disabled={loading}
              >
                🔄 Refresh
              </Button>
            </div>
            
            <Button 
              onClick={handleClose}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ToleranceSettingsModal;